import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../firebase/config";
import {
  collection,
  getDocs,
  query,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import moment from "moment";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import {
  DocumentIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

const styles = {
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  filterContainer: {
    display: "flex",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  filterInput: {
    maxWidth: 300,
    width: "100%",
  },
  select: {
    width: "100%",
    height: 56,
    padding: "0 12px",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  tableContainer: {
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    overflowX: "auto",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    padding: 12,
    textAlign: "left",
    fontFamily: FONTS.bold,
    fontSize: 16,
    color: COLORS.text,
    borderBottom: `1px solid ${COLORS.tertiary}`,
  },
  td: {
    padding: 12,
    borderBottom: `1px solid ${COLORS.tertiary}`,
    fontFamily: FONTS.regularInter,
    fontSize: 16,
    color: COLORS.text,
  },
  icon: {
    marginRight: 8,
    verticalAlign: "middle",
    width: 20,
    height: 20,
  },
  weekHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    border: `1px solid ${COLORS.tertiary}`,
    marginBottom: 12,
    cursor: "pointer",
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& h2, & svg": {
        color: COLORS.white,
        stroke: COLORS.white,
      },
    },
  },
  weekTitle: {
    fontFamily: FONTS.bold,
    fontSize: 18,
    color: COLORS.text,
  },
  noData: {
    textAlign: "center",
    padding: 16,
    fontFamily: FONTS.regularInter,
    fontSize: 18,
    color: COLORS.gray,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    filterContainer: {
      flexDirection: "column",
      gap: 8,
    },
    filterInput: {
      maxWidth: "100%",
    },
    select: {
      height: 48,
    },
    weekHeader: {
      padding: 10,
    },
  },
};

// Resolve the best available job name for an invoice. Older log/action
// invoices stored "No job name" (the source job was lost), so fall back to any
// other field that carries it before giving up.
const resolveJobName = (inv) => {
  const j = inv?.data?.jobName;
  if (j && j !== "No job name") return j;
  if (inv?.jobName) return inv.jobName;
  if (inv?.data?.jobNumber) return inv.data.jobNumber;
  return "No job";
};

const AdminInvoiceManagementScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [weekFilter, setWeekFilter] = useState("");
  const [numberFilter, setNumberFilter] = useState("");
  const [jobNameFilter, setJobNameFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [loading, setLoading] = useState(false);
  const functions = getFunctions();
  const generateInvoicePDF = httpsCallable(functions, "generateInvoicePDF");

  useEffect(() => {
    if (!user || userType !== "admin") {
      navigate("/");
      return;
    }

    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, "invoices"));
        const invoiceList = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((invoice) => {
            if (!invoice.weekStart || !invoice.createdAt || !invoice.data) {
              console.warn(
                `Invalid invoice (ID: ${invoice.id}): Missing weekStart, createdAt, or data`
              );
              return false;
            }
            return true;
          });
        if (invoiceList.length < querySnapshot.docs.length) {
          alert(
            "Some invoices were excluded due to missing data. Please contact support."
          );
        }
        setInvoices(invoiceList);
        setFilteredInvoices(invoiceList);
      } catch (error) {
        console.error("Error fetching invoices:", error);
        alert("Failed to fetch invoices: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [user, userType, navigate]);

  useEffect(() => {
    let filtered = invoices;

    if (numberFilter) {
      filtered = filtered.filter((invoice) =>
        invoice.invoiceNumber
          ?.toLowerCase()
          .includes(numberFilter.toLowerCase())
      );
    }

    if (jobNameFilter) {
      filtered = filtered.filter((invoice) =>
        resolveJobName(invoice)
          .toLowerCase()
          .includes(jobNameFilter.toLowerCase())
      );
    }

    if (typeFilter) {
      filtered = filtered.filter((invoice) =>
        typeFilter === "manual" ? invoice.isManual : invoice.type === typeFilter
      );
    }

    if (weekFilter) {
      filtered = filtered.filter((invoice) => {
        if (!invoice.weekStart) return false;
        const weekStart = moment(invoice.weekStart.toDate()).format(
          "YYYY-MM-DD"
        );
        return weekStart === weekFilter;
      });
    }

    setFilteredInvoices(filtered);
  }, [weekFilter, numberFilter, jobNameFilter, typeFilter, invoices]);

  const getWeekOptions = () => {
    const weeks = new Set();
    invoices.forEach((invoice) => {
      if (!invoice.weekStart) return;
      const weekStart = moment(invoice.weekStart.toDate()).format("YYYY-MM-DD");
      weeks.add(weekStart);
    });
    return Array.from(weeks).sort().reverse();
  };

  const toggleWeek = (weekStart) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [weekStart]: !prev[weekStart],
    }));
  };

  const handleDownload = async (invoice) => {
    setLoading(true);
    try {
      const oneMonthAgo = moment().subtract(1, "month").toDate();
      let url;
      if (
        invoice.createdAt?.toDate() >= oneMonthAgo &&
        invoice.pdfStoragePath
      ) {
        try {
          const fileRef = ref(storage, invoice.pdfStoragePath);
          url = await getDownloadURL(fileRef);
        } catch (storageError) {
          if (storageError.code === "storage/object-not-found") {
            console.log(
              `PDF ${invoice.pdfStoragePath} not found, regenerating...`
            );
          } else {
            throw storageError;
          }
        }
      }

      if (!url) {
        const response = await generateInvoicePDF({
          ...invoice.data,
          invoiceNumber: invoice.invoiceNumber,
          type: invoice.type,
          email: invoice.email,
          userId: invoice.userId || "Unknown Driver ID",
        });
        const { pdfBase64, invoiceNumber } = response.data;
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        url = URL.createObjectURL(blob);

        const now = moment();
        const invoiceRef = doc(db, "invoices", invoice.invoiceNumber);
        await updateDoc(invoiceRef, {
          createdAt: Timestamp.fromDate(now.toDate()),
          pdfStoragePath: `invoices/${invoiceNumber}.pdf`,
          weekStart: Timestamp.fromDate(
            now.startOf("isoWeek").add(1, "day").toDate()
          ),
          weekEnd: Timestamp.fromDate(
            now.startOf("isoWeek").add(8, "days").endOf("day").toDate()
          ),
        });
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice_${invoice.invoiceNumber}.pdf`;
      // Open in a new tab. For cross-origin Storage URLs the browser ignores
      // `download` and would otherwise navigate the current tab to the PDF,
      // so pressing Back reloads the SPA; _blank keeps the app tab intact.
      link.target = "_blank";
      link.rel = "noopener";
      link.click();
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const groupInvoicesByWeek = () => {
    const grouped = {};
    filteredInvoices.forEach((invoice) => {
      if (!invoice.weekStart) return;
      const weekStart = moment(invoice.weekStart.toDate()).format("YYYY-MM-DD");
      if (!grouped[weekStart]) {
        grouped[weekStart] = [];
      }
      grouped[weekStart].push(invoice);
    });
    return grouped;
  };

  const groupedInvoices = groupInvoicesByWeek();

  return (
    <div style={styles.container}>
      <div style={styles.headerContainer}>
        <h1 style={styles.title}>Invoice Management</h1>
      </div>
      <div style={styles.filterContainer}>
        <CustomInput
          value={numberFilter}
          onChangeText={setNumberFilter}
          placeholder="Search by invoice number"
          icon="magnifying-glass"
          style={styles.filterInput}
        />
        <CustomInput
          value={jobNameFilter}
          onChangeText={setJobNameFilter}
          placeholder="Search by job name"
          icon="briefcase"
          style={styles.filterInput}
        />
        <div style={styles.filterInput}>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All Types</option>
            <option value="log">Driver Log</option>
            <option value="order">Order</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        <div style={styles.filterInput}>
          <select
            value={weekFilter}
            onChange={(e) => setWeekFilter(e.target.value)}
            style={styles.select}
          >
            <option value="">All Weeks</option>
            {getWeekOptions().map((week) => (
              <option key={week} value={week}>
                {moment(week).format("MM/DD/YYYY")} -{" "}
                {moment(week).add(7, "days").format("MM/DD/YYYY")}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div style={styles.loader}>
          <div style={styles.loadingSpinner}></div>
          <span>Loading...</span>
        </div>
      ) : Object.keys(groupedInvoices).length === 0 ? (
        <div style={styles.noData}>No invoices found</div>
      ) : (
        Object.keys(groupedInvoices)
          .sort()
          .reverse()
          .map((weekStart) => (
            <div key={weekStart} style={{ marginBottom: 12 }}>
              <div
                style={styles.weekHeader}
                onClick={() => toggleWeek(weekStart)}
              >
                <h2 style={styles.weekTitle}>
                  Week of {moment(weekStart).format("MM/DD/YYYY")} -{" "}
                  {moment(weekStart).add(7, "days").format("MM/DD/YYYY")}
                </h2>
                {expandedWeeks[weekStart] ? (
                  <ChevronUpIcon width={24} height={24} stroke={COLORS.text} />
                ) : (
                  <ChevronDownIcon
                    width={24}
                    height={24}
                    stroke={COLORS.text}
                  />
                )}
              </div>
              {expandedWeeks[weekStart] && (
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Invoice Number</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Job Name</th>
                        <th style={styles.th}>Total Price</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedInvoices[weekStart].map((invoice) => (
                        <tr
                          key={invoice.invoiceNumber}
                          style={{
                            backgroundColor: `${COLORS.secondary}f0`,
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: COLORS.primary,
                              "& td": {
                                color: COLORS.white,
                              },
                              "& svg": {
                                stroke: COLORS.white,
                              },
                            },
                          }}
                        >
                          <td style={styles.td}>{invoice.invoiceNumber}</td>
                          <td style={styles.td}>
                            {invoice.isManual ? (
                              <>
                                <DocumentIcon
                                  width={20}
                                  height={20}
                                  stroke={COLORS.primary}
                                  style={styles.icon}
                                />
                                Manual
                              </>
                            ) : invoice.type === "log" ? (
                              "Driver Log"
                            ) : (
                              "Order"
                            )}
                          </td>
                          <td style={styles.td}>
                            {resolveJobName(invoice)}
                          </td>
                          <td style={styles.td}>
                            ${invoice.data?.totalPrice?.toFixed(2) || "0.00"}
                          </td>
                          <td style={styles.td}>
                            <CustomButton
                              title="Download"
                              onPress={() => handleDownload(invoice)}
                              disabled={loading}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 12,
                                transition: "all 0.3s ease",
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
      )}
    </div>
  );
};

export default AdminInvoiceManagementScreen;
